import { useCallback } from 'react';
import { App } from 'antd';
import { useForm } from 'antd/es/form/Form';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CalendarEvent, CalendarModalBody } from 'entities/calendar';

import { CreateCalendarEventForm } from '../ui';

type FormValues = Omit<CalendarEvent, 'id' | 'startDate' | 'endDate'> & {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
};

const useCreateEventModal = (calendarId: string, cleanUpFn: () => void): ((dateRange: dayjs.Dayjs[]) => void) => {
  const { modal } = App.useApp();
  const [form] = useForm<FormValues>();

  const { t } = useTranslation('Calendar');

  const openCreateModal = useCallback(
    (dateRange: dayjs.Dayjs[]): void => {
      const instance = modal.confirm({
        centered: true,
        closable: true,
        maskClosable: true,
        okButtonProps: { htmlType: 'submit' },
        cancelButtonProps: { htmlType: 'reset' },
        okText: t('calendarModal.okBtn.text'),
        width: 450,
        icon: null,
        onOk: () => {
          return Promise.reject();
        },
        afterClose: () => {
          form.resetFields();
          cleanUpFn();
        },
        modalRender: (dom) => (
          <CreateCalendarEventForm
            datePeriod={dateRange}
            form={form}
            dom={dom}
            instance={instance}
            calendarId={calendarId}
          />
        ),
        content: <CalendarModalBody form={form} calendarId={calendarId} />,
      });
    },
    [calendarId, cleanUpFn, form, modal, t],
  );

  return openCreateModal;
};

export { useCreateEventModal };
